"use client";
import type { Theme } from "@mui/material/styles";

import { textColors } from "./palette";

const fontFamilyHeading = "'Figtree', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const fontFamilyBody = "'Work Sans', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

const headingCommon = {
  color: textColors.secondary,
  fontFamily: fontFamilyHeading,
  fontWeight: 500,
};

const bodyCommon = {
  color: textColors.primary,
  fontFamily: fontFamilyBody,
  letterSpacing: "-0.5px",
  fontWeight: 400,
};

const bodyL = {
  ...bodyCommon,
  fontSize: "1rem",
  lineHeight: "1.5rem",
  ["@media (min-width:600px)"]: {
    fontSize: "1.125rem",
    lineHeight: "1.5em",
  },
  ["@media (min-width:960px)"]: {
    fontSize: "1.25rem",
    lineHeight: "1.75rem",
  },
};

const bodyM = {
  ...bodyCommon,
  fontSize: "0.875rem",
  lineHeight: "1.25rem",
  "@media (min-width:600px)": {
    fontSize: "1rem",
    lineHeight: "1.5em",
  },
  "@media (min-width:960px)": {
    fontSize: "1.125rem",
    lineHeight: "1.5rem",
  },
};

const bodyS = {
  ...bodyCommon,
  fontSize: "0.875rem",
  lineHeight: "1.25rem",
  "@media (min-width:960px)": {
    fontSize: "1rem",
    lineHeight: "1.5em",
  },
};

const bodyXS = {
  ...bodyCommon,
  fontSize: "0.75rem",
  lineHeight: "1rem",
};

export const typography: Partial<Theme["typography"]> = {
  headingXXL: {
    ...headingCommon,
    fontSize: "3.5rem",
    lineHeight: "4.25rem",
    component: "h1",
    "@media (min-width:600px)": {
      fontSize: "3.75rem",
      lineHeight: "5rem",
    },
    "@media (min-width:960px)": {
      fontSize: "4.75rem",
      lineHeight: "5.9375rem",
    },
  },
  headingXL: {
    ...headingCommon,
    fontSize: "2.75rem",
    lineHeight: "3.5rem",
    component: "h2",
    "@media (min-width:600px)": {
      fontSize: "3.5rem",
      lineHeight: "4.2rem",
    },
    "@media (min-width:960px)": {
      fontSize: "4rem",
      lineHeight: "5rem",
    },
  },
  headingL: {
    ...headingCommon,
    fontSize: "2.25rem",
    lineHeight: "2.75rem",
    component: "h3",
    "@media (min-width:600px)": {
      fontSize: "2.5rem",
      lineHeight: "3rem",
    },
    "@media (min-width:960px)": {
      fontSize: "3rem",
      lineHeight: "3.75rem",
    },
  },
  headingM: {
    ...headingCommon,
    fontSize: "1.75rem",
    lineHeight: "2rem",
    "@media (min-width:600px)": {
      fontSize: "2rem",
      lineHeight: "2.5rem",
    },
    "@media (min-width:960px)": {
      fontSize: "2.5rem",
      lineHeight: "3rem",
    },
  },
  headingS: {
    ...headingCommon,
    fontSize: "1.5rem",
    lineHeight: "1.75rem",
    "@media (min-width:600px)": {
      fontSize: "1.75rem",
      lineHeight: "2rem",
    },
    "@media (min-width:960px)": {
      fontSize: "2rem",
      lineHeight: "2.5rem",
    },
  },
  headingXS: {
    ...headingCommon,
    fontSize: "1.25rem",
    lineHeight: "1.5rem",
    ["@media (min-width:600px)"]: {
      fontSize: "1.5rem",
      lineHeight: "1.75rem",
    },
    ["@media (min-width:960px)"]: {
      fontSize: "1.75rem",
      lineHeight: "2rem",
    },
  },
  bodyL,
  bodyLSemiBold: {
    ...bodyL,
    fontWeight: 500,
  },
  bodyLBold: {
    ...bodyL,
    fontWeight: 600,
  },
  bodyM,
  bodyMSemiBold: {
    ...bodyM,
    fontWeight: 500,
  },
  bodyMBold: {
    ...bodyM,
    fontWeight: 600,
  },
  bodyS,
  bodySSemiBold: {
    ...bodyS,
    fontWeight: 500,
  },
  bodySBold: {
    ...bodyS,
    fontWeight: 600,
  },
  bodyXS,
  bodyXSSemiBold: {
    ...bodyXS,
    fontWeight: 500,
  },
  bodyXSBold: {
    ...bodyXS,
    fontWeight: 600,
  },
};
